import { ERROR_CODE_VALUES, type ErrorCode } from '@napaicp/shared'
import { z } from 'zod'
import { type ZodType } from 'zod'

/**
 * Every response uses one of two shapes:
 *   success -> { "data": ..., "meta": { ... } }
 *   failure -> { "error": { "code": ..., "message": ..., "details": { ... } } }
 *
 * Raw exceptions and stack traces never cross this boundary.
 */
export const apiMetaSchema = z.looseObject({
  requestId: z.string().min(1),
})

export type ApiMeta = z.infer<typeof apiMetaSchema>

export const apiErrorBodySchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODE_VALUES),
    message: z.string().min(1),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
  /** Present on every API response; optional here for defensive parsing of a broken proxy response. */
  meta: apiMetaSchema.optional(),
})

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>

export function apiSuccessSchema<T extends ZodType>(dataSchema: T) {
  return z.object({
    data: dataSchema,
    meta: apiMetaSchema,
  })
}

export interface ApiSuccess<TData> {
  data: TData
  meta: ApiMeta
}

export type ApiResponse<TData> = ApiSuccess<TData> | ApiErrorBody

export function isApiError<TData>(response: ApiResponse<TData>): response is ApiErrorBody {
  return 'error' in response
}

export interface ApiErrorDetail {
  code: ErrorCode
  message: string
  details?: Record<string, unknown>
}
